// ===========================================
// BUSTED! BLACKJACK - ANIMATION QUEUE PROCESSOR
// ===========================================
// Platform-agnostic queue processor for game events
// Processes events sequentially, calling renderers for animations
// ===========================================
import { DEFAULT_WEB_TIMING, getEventTiming } from './animation-timing.js';
// ===========================================
// ANIMATION QUEUE PROCESSOR CLASS
// ===========================================
export class AnimationQueueProcessor {
    queue = [];
    renderer = null;
    options;
    isProcessing = false;
    isPaused = false;
    lastProcessedSequence = -1;
    constructor(options = {}) {
        this.options = {
            maxQueueSize: 100,
            timingConfig: DEFAULT_WEB_TIMING,
            instantMode: false,
            ...options,
        };
    }
    // ----- Renderer Management -----
    setRenderer(renderer) {
        this.renderer = renderer;
    }
    getRenderer() {
        return this.renderer;
    }
    // ----- Queue Management -----
    /**
     * Add an event to the queue
     * Events are sorted by sequence number to handle out-of-order delivery
     */
    enqueue(event) {
        // Reset sequence tracking on new round (ROUND_STARTED always has seq 1)
        if (event.type === 'ROUND_STARTED' && event.sequenceNumber === 1) {
            this.lastProcessedSequence = -1;
        }
        // Skip if we've already processed this event
        if (event.sequenceNumber <= this.lastProcessedSequence) {
            return;
        }
        // Check if event is already in queue
        const existing = this.queue.find(q => q.event.sequenceNumber === event.sequenceNumber);
        if (existing) {
            return;
        }
        const queuedEvent = {
            event,
            addedAt: Date.now(),
            status: 'pending',
        };
        // Insert in sorted order by sequence number
        const insertIndex = this.queue.findIndex(q => q.event.sequenceNumber > event.sequenceNumber);
        if (insertIndex === -1) {
            this.queue.push(queuedEvent);
        }
        else {
            this.queue.splice(insertIndex, 0, queuedEvent);
        }
        // Trim queue if it exceeds max size
        while (this.queue.length > this.options.maxQueueSize) {
            const removed = this.queue.shift();
            if (removed) {
                removed.status = 'skipped';
            }
        }
        this.notifyQueueStateChange();
        // Start processing if not already
        if (!this.isProcessing && !this.isPaused) {
            this.processNext();
        }
    }
    /**
     * Add multiple events at once (e.g., on reconnect/catchup)
     */
    enqueueBatch(events) {
        // Sort by sequence number first
        const sorted = [...events].sort((a, b) => a.sequenceNumber - b.sequenceNumber);
        for (const event of sorted) {
            this.enqueue(event);
        }
    }
    /**
     * Clear all pending events
     */
    clear() {
        this.queue = [];
        this.isProcessing = false;
        this.renderer?.onQueueCleared?.();
        this.notifyQueueStateChange();
    }
    /**
     * Pause queue processing (events still accumulate)
     */
    pause() {
        this.isPaused = true;
    }
    /**
     * Resume queue processing
     */
    resume() {
        this.isPaused = false;
        if (!this.isProcessing && this.queue.length > 0) {
            this.processNext();
        }
    }
    // ----- Queue State -----
    getPendingCount() {
        return this.queue.filter(q => q.status === 'pending').length;
    }
    isQueueProcessing() {
        return this.isProcessing;
    }
    isQueuePaused() {
        return this.isPaused;
    }
    getLastProcessedSequence() {
        return this.lastProcessedSequence;
    }
    /**
     * Get all pending events (for debugging/UI)
     */
    getPendingEvents() {
        return this.queue
            .filter(q => q.status === 'pending')
            .map(q => q.event);
    }
    // ----- Processing -----
    async processNext() {
        if (this.isPaused || this.isProcessing) {
            return;
        }
        // Find next pending event
        const nextQueued = this.queue.find(q => q.status === 'pending');
        if (!nextQueued) {
            this.renderer?.onQueueEmpty?.();
            this.notifyQueueStateChange();
            return;
        }
        this.isProcessing = true;
        nextQueued.status = 'processing';
        nextQueued.processedAt = Date.now();
        this.notifyQueueStateChange();
        const event = nextQueued.event;
        const startTime = Date.now();
        // Log timing for resolution-related events
        if (['HAND_RESOLVED', 'CHIPS_PAID', 'CHIPS_COLLECTED'].includes(event.type)) {
            console.log(`[TIMING] Queue processing ${event.type} at ${startTime} (seq: ${event.sequenceNumber})`);
        }
        try {
            // Check if renderer can handle this event
            const canRender = this.renderer?.canRender(event) ?? false;
            if (this.renderer && canRender) {
                if (!this.options.instantMode) {
                    // Render the animation and wait for completion
                    await this.renderer.renderEvent(event);
                }
            }
            // Apply minimum duration from timing config
            const minDuration = getEventTiming(event, this.options.timingConfig);
            const elapsed = Date.now() - startTime;
            if (elapsed < minDuration && !this.options.instantMode) {
                if (['HAND_RESOLVED', 'CHIPS_PAID', 'CHIPS_COLLECTED'].includes(event.type)) {
                    console.log(`[TIMING] ${event.type} waiting additional ${minDuration - elapsed}ms (minDuration: ${minDuration}, elapsed: ${elapsed})`);
                }
                await this.delay(minDuration - elapsed);
            }
        }
        catch (error) {
            this.options.onRenderError?.(event, error);
        }
        // Mark completed
        nextQueued.status = 'completed';
        nextQueued.completedAt = Date.now();
        this.lastProcessedSequence = event.sequenceNumber;
        const duration = Date.now() - startTime;
        this.options.onEventProcessed?.(event, duration);
        // Remove completed events from queue (keep last few for debugging)
        this.pruneCompletedEvents();
        // Process next
        this.isProcessing = false;
        this.processNext();
    }
    pruneCompletedEvents() {
        const completedCount = this.queue.filter(q => q.status === 'completed' || q.status === 'skipped').length;
        // Keep last 10 completed events for debugging
        if (completedCount > 10) {
            this.queue = this.queue.filter(q => q.status === 'pending' || q.status === 'processing');
        }
    }
    delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
    notifyQueueStateChange() {
        this.options.onQueueStateChange?.(this.getPendingCount(), this.isProcessing);
    }
    // ----- Configuration -----
    setTimingConfig(config) {
        this.options.timingConfig = config;
    }
    setInstantMode(instant) {
        this.options.instantMode = instant;
    }
    // ----- Catchup Mode -----
    /**
     * Fast-forward through events to catch up to current state
     * Useful after reconnection
     */
    async catchUp(targetSequence) {
        this.options.instantMode = true;
        while (this.lastProcessedSequence < targetSequence && this.queue.length > 0) {
            await this.processNext();
        }
        this.options.instantMode = false;
    }
}
// ===========================================
// SINGLETON FACTORY (optional convenience)
// ===========================================
let defaultProcessor = null;
export function getDefaultProcessor() {
    if (!defaultProcessor) {
        defaultProcessor = new AnimationQueueProcessor();
    }
    return defaultProcessor;
}
export function resetDefaultProcessor() {
    defaultProcessor?.clear();
    defaultProcessor = null;
}
//# sourceMappingURL=AnimationQueueProcessor.js.map